import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DataTable, ActiveFilters } from "../components/DataTable";
import { useServerList } from "../hooks/useServerList";

const STATUS_OPTS = [
  { v: "all", l: "Semua Status QC" },
  { v: "waiting_qc", l: "Menunggu QC" },
  { v: "passed", l: "QC Passed" },
  { v: "rework", l: "Rework" },
  { v: "reject", l: "Reject" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTS.map((o) => [o.v, o.l]));
const COLORS = { waiting_qc: "text-slate-500", passed: "text-status-passed", rework: "text-status-rework", reject: "text-status-reject" };

export default function Stock() {
  const [warehouses, setWarehouses] = useState([]);
  const [wh, setWh] = useState("all");
  const [status, setStatus] = useState("all");
  const [color, setColor] = useState("all");
  const [size, setSize] = useState("all");
  const [opts, setOpts] = useState({ colors: [], sizes: [] });

  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/stock/filter-options").then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const filters = React.useMemo(() => {
    const f = {};
    if (wh !== "all") f.warehouse_id = wh;
    if (status !== "all") f.qc_status = status;
    if (color !== "all") f.color = color;
    if (size !== "all") f.size = size;
    return f;
  }, [wh, status, color, size]);

  const { q, setQ, items, total, pages, page, setPage, limit, setLimit, sort, toggleSort, loading, error } =
    useServerList("/stock", { filters, sort: "product_name", dir: "asc" });

  const whName = (id) => warehouses.find((w) => w.id === id)?.name || id;
  const columns = [
    { key: "product_name", label: "Produk", sortable: true, render: (r) => <span className="font-semibold text-cocoa">{r.product_name}</span> },
    { key: "color", label: "Warna", sortable: true },
    { key: "size", label: "Ukuran", sortable: true },
    { key: "warehouse_name", label: "Gudang" },
    { key: "qc_status", label: "Status QC", render: (r) => <span className={`font-medium ${COLORS[r.qc_status] || ""}`}>{r.qc_status_label}</span> },
    { key: "qty", label: "Qty", align: "right", sortable: true, render: (r) => <span className="font-bold text-cocoa">{r.qty}</span> },
  ];

  const chips = [];
  if (wh !== "all") chips.push({ key: "wh", label: `Gudang: ${whName(wh)}`, onClear: () => setWh("all") });
  if (status !== "all") chips.push({ key: "status", label: `QC: ${STATUS_LABEL[status]}`, onClear: () => setStatus("all") });
  if (color !== "all") chips.push({ key: "color", label: `Warna: ${color}`, onClear: () => setColor("all") });
  if (size !== "all") chips.push({ key: "size", label: `Ukuran: ${size}`, onClear: () => setSize("all") });
  const resetAll = () => { setWh("all"); setStatus("all"); setColor("all"); setSize("all"); };

  return (
    <div data-testid="stock-page">
      <PageHeader title="Stok Gudang" subtitle="Stok fisik per varian, gudang, dan status QC" />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / warna / ukuran..." className="pl-9 bg-white" data-testid="stock-search" />
        </div>
        <Select value={wh} onValueChange={setWh}>
          <SelectTrigger className="w-44 bg-white" data-testid="stock-warehouse-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Gudang</SelectItem>
            {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 bg-white" data-testid="stock-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={color} onValueChange={setColor}>
          <SelectTrigger className="w-40 bg-white" data-testid="stock-color-filter"><SelectValue placeholder="Warna" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Warna</SelectItem>{opts.colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={size} onValueChange={setSize}>
          <SelectTrigger className="w-36 bg-white" data-testid="stock-size-filter"><SelectValue placeholder="Ukuran" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Ukuran</SelectItem>{opts.sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <ActiveFilters chips={chips} onReset={chips.length ? resetAll : undefined} />

      <DataTable
        testid="stock-table"
        columns={columns}
        rows={items}
        loading={loading}
        error={error}
        sort={sort}
        onSort={toggleSort}
        rowKey={(r) => `${r.variant_id}-${r.warehouse_id}-${r.qc_status}`}
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
        searching={!!q}
        emptyLabel="Belum ada stok."
      />
    </div>
  );
}
